/**
 * **编辑器注册表** —— 「有哪些窗口」的**单一来源**。
 *
 * ```text
 *   CORE_EDITOR_META      内核自带的那几条（graph / canvas / … / theme）
 *        ＋
 *   包贡献的 editors       NodePackage.editors（代码包才能给，见 types/editor.ts）
 *        ↓  registerEditorContributions()
 *   EDITOR_META           合并结果 —— 菜单 / 校验 / 序列化 / 渲染都读它
 *   EDITOR_ORDER          按 digit 排好的清单（窗口类型菜单、快捷键）
 *   IMPLEMENTED_EDITORS   只留已实现的（手机档模式栏）
 * ```
 *
 * ## 为什么要有合并这一步
 *
 * 「页面编辑器」这种东西必须是**独立的一种 Area 编辑器**，而它属于打印包，
 * 不属于内核（见 `docs/package-boundaries.md` §0：打印要作为节点包做，
 * 不能把通用编辑器改成打印编辑器的形状）。所以「有哪些编辑器」不能再是
 * 一张写死的表 —— 它得是「内核的 + 包注册的」。
 *
 * ## 两条纪律（和 `NODE_TYPES` 那边一模一样）
 *
 * ① **原地更新**。`EDITOR_META` / `EDITOR_ORDER` / `IMPLEMENTED_EDITORS`
 *    被好几个模块 `import` 走了（菜单、`NpArea`、验证套件）——
 *    **换一个对象/数组它们就看不到新目录**（表现：包装上了但窗口类型菜单里没有）。
 *    所以是 `delete` 键 + `Object.assign`、`length = 0` + `push(...)`。
 *
 * ② **重复要抛错，不许「后者覆盖前者」**。两个包抢同一个编辑器类型、
 *    或者包抢了内核已有的类型，都是必须当场知道的错误：静默覆盖的表现是
 *    「加载顺序变了，某个窗口的行为就变了」，那种 bug 没有任何现场。
 */
import { reactive, shallowReactive } from 'vue'
import {
  ALL_REGIONS,
  type CoreEditorType,
  type EditorContributionSource,
  type EditorMeta,
  type EditorType,
  type PanelSide,
  type PanelTabDef,
  type RegionKey,
  type RegionState,
} from '../types/editor'

/* ══════════════════════════════════════════════════════════════════
   内核自带的那几条
   ══════════════════════════════════════════════════════════════════ */

/**
 * 内核的编辑器目录。
 *
 * ⚠️ `Record<CoreEditorType, EditorMeta>` 是**封闭**的（内核那 8 条一个不多
 * 一个不少）—— 合并之后的 `EDITOR_META` 才是开放的。这条类型上的区别
 * 就是「内核自带」和「包贡献」的分界，别把它抹平。
 */
export const CORE_EDITOR_META: Record<CoreEditorType, EditorMeta> = {
  graph: {
    label: '节点图',
    icon: '<rect x="3" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="8.5" y="16" width="7" height="5" rx="1.5"/><path d="M6.5 8v5h11V8"/><path d="M12 13v3"/>',
    digit: 1,
    implemented: true,
    /* 节点图是主工作区：header + 小地图覆盖层 */
    regions: ['header', 'minimap'],
    /*
     * 右侧竖排 tab（Blender 的 N 侧栏）。
     *
     * ⚠️ 只有「属性」一页。原来还有一页「视图」（列画布显示项），**删掉了** ——
     * 实测反馈：「右侧去掉视图tab面板，因为小地图在窗口顶栏有开关了，
     * 没必要做多余的」。同一件事两个入口是纯粹的冗余。
     *
     * ⚠️ **没有左侧 tab** —— 所以左侧那个面板按钮**不会出现**。
     * 这条规则是通用的：`NpArea` 按 `panels[side]` 有没有内容决定
     * 要不要渲染那一侧的开关（见 `panelSides`）。空面板不给按钮，
     * 免得点开一个空壳。
     */
    panels: {
      right: [
        { id: 'properties', label: '属性', icon: '<path d="M4 7h6M14 7h6M4 17h10M18 17h2"/><circle cx="12" cy="7" r="2"/><circle cx="16" cy="17" r="2"/>' },
      ],
    },
  },
  /*
   * ⚠️ 这里原来有一条 `canvas`（「版面」，digit 2）—— **删掉了**（2027）。
   *
   * 它当时是占位（`implemented: false`），因为「版面画布」看着像内核能力
   * （`package-boundaries.md` §10：平移缩放 / 命中判定 / 撤销重做 UI 域和
   * 打印域都要）。但真做的时候发现**「版面」本身就是域概念**：
   *
   *   · 页面的纸张 / DPI / 静区、元素有哪些种类 —— 只有打印域说得清（§10 的判据：
   *     「换个域还要不要它」）；
   *   · UI 设计域的画布长的是另一副样子（约束 / 自适应 / 组件树），
   *     和「100×150mm 的标签页」不是同一个东西；
   *   · 而**通用的那一半**（指针手势 / 视口 / 命中 / 框选）已经在
   *     `graph/` 里了（换画布时一行没重写，见 `package-boundaries.md` §9）。
   *
   * 所以「页面编辑器」是**包贡献的编辑器**（`print.page`，见
   * `domains/print/`），不是内核的第 9 条。留一条永远点不到的灰色入口，
   * 只会让人以为「版面功能缺失」。
   *
   * ⚠️ 顺带把 `digit` 重排成连续的 1..7：序号只决定菜单顺序（将来是
   * Ctrl+数字），**没有持久化**（布局树里记的是编辑器**标识**）——
   * 留一个空洞没有任何好处。
   */
  outliner: {
    label: '大纲',
    icon: '<path d="M4 6h3M4 12h3M4 18h3"/><path d="M10 6h10M10 12h10M10 18h10"/>',
    digit: 2,
    implemented: false,
    regions: ['header'],
    defaultRegions: { minimap: false },
  },
  properties: {
    label: '属性',
    icon: '<path d="M4 7h6M14 7h6M4 17h10M18 17h2"/><circle cx="12" cy="7" r="2"/><circle cx="16" cy="17" r="2"/>',
    digit: 3,
    implemented: true,
    /*
     * 属性编辑器只读**模板与选中节点的元信息**，不需要画布那套 Region ——
     * 它自己的内容就在编辑器主体里。
     */
    regions: ['header'],
    defaultRegions: { minimap: false },
  },
  console: {
    label: '输出',
    icon: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9l3 3-3 3M13 15h4"/>',
    digit: 4,
    implemented: true,
    regions: ['header'],
    defaultRegions: { minimap: false },
  },
  library: {
    label: '节点库',
    icon: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
    digit: 5,
    implemented: false,
    regions: ['header'],
    defaultRegions: { minimap: false },
  },
  devices: {
    label: '设备',
    icon: '<path d="M7 8V4h10v4"/><rect x="3" y="8" width="18" height="8" rx="2"/><path d="M7 16h10v4H7z"/>',
    digit: 6,
    implemented: false,
    regions: ['header'],
    defaultRegions: { minimap: false },
  },
  theme: {
    label: '主题',
    icon: '<circle cx="12" cy="12" r="9"/><circle cx="9" cy="9.5" r="1.3"/><circle cx="15" cy="9.5" r="1.3"/><circle cx="9.5" cy="15" r="1.3"/><circle cx="15" cy="15" r="1.3"/>',
    digit: 7,
    implemented: true,
    /*
     * 主题编辑器是**一个正常的工作区窗口**，不是浮层。
     *
     * ⚠️ 它原来是一个 `position: fixed` 的弹窗（`NpThemePanel`）。
     * 弹窗在调主题这件事上有个硬伤：**它盖住的正是你要看的东西** ——
     * 调节点外观时弹窗压在节点上，只能靠"关掉看一眼再打开"迭代。
     * 做成 Area 之后它和画布并排，改一个值直接看到画布上的变化，
     * 而且能最大化 / 分屏 / 存进工作区，和别的窗口一视同仁。
     */
    regions: ['header'],
    defaultRegions: { minimap: false },
  },
  /*
   * ── 文档（2027 加）────────────────────────────────────────────
   *
   * 用户原话：
   *
   * > 我们需要一个新的窗口，类似于管理页面也可以用于首页，被管理文档给文档
   * > 修改名称……每个文档有唯一的 UUID，加载文档的时候，如果发现 UUID 有
   * > 重复的时候弹窗提醒是否覆盖。
   *
   * ⚠️ 它是**内核**的编辑器，不是包贡献的：文档库管的是"这个编辑器里有哪些
   * 文档"，和节点图一样属于宿主自己的东西 —— 换个领域的包来，它照样成立。
   *
   * ⚠️ 它**不自动开**（用户选的那条）：窗口类型菜单里多一项，可以当首页摆在
   * 第一个 Area，但默认布局不变 —— 现有用户的工作区与快捷键一个都不受影响。
   *
   * 序号排在最后（8）：菜单顺序 = digit，而"文件"这一档的入口习惯在最前面，
   * 但内核那 7 条已经各就各位了 —— 新来的一条排最后，不动老的。
   */
  documents: {
    label: '文档',
    icon: '<path d="M6 3h8l4 4v14H6z"/><path d="M14 3v4h4"/><path d="M9 12h6M9 16h4"/>',
    digit: 8,
    implemented: true,
    regions: ['header'],
    defaultRegions: { minimap: false },
    /*
     * ⚠️ **它必须有 tips**：底部提示条显示的是"当前窗口能干什么"，
     * 而这个窗口是**默认第一个**（用户要的"文档窗口做成默认窗口"）——
     * 不给的话，打开编辑器时那条提示是**空的**（实测：`verify:render`
     * 那条"全局提示条里有快捷键胶囊"当场红）。
     *
     * 写的是这一页真正有的操作：没有键盘快捷键，就用点击动作当"键位"
     * （提示条本来就允许 `keys` 是一句话 —— 打印包的页面编辑器也这么写）。
     */
    tips: [
      { keys: '双击卡片名', label: '改名', touchKeys: '双击卡片名', touchLabel: '改名' },
      { keys: '＋ 新建文档', label: '把当前画布收进库里' },
      { keys: '文件 → 示例文件', label: '打开一份示例看看' },
    ],
  },
}

/** 内核那几条按 digit 排好的顺序（合并时贡献排在它们后面） */
export const CORE_EDITOR_ORDER: readonly CoreEditorType[] = (
  Object.keys(CORE_EDITOR_META) as CoreEditorType[]
).sort((x, y) => CORE_EDITOR_META[x].digit - CORE_EDITOR_META[y].digit)

/* ══════════════════════════════════════════════════════════════════
   合并结果（原地更新，见文件头 ①）
   ══════════════════════════════════════════════════════════════════ */

/**
 * **全部**编辑器的元信息：内核的 + 包贡献的。查不到的返回 `undefined`。
 *
 * ⚠️ **它是响应式的**（`shallowReactive`），而且这一点是必须的：
 * 装/卸一个贡献编辑器的包**不会改动布局树**（窗口记的是意图，
 * 显示的由 `editorOf()` 现算）—— 于是"界面要跟着变"这件事
 * 只能靠"读注册表"这个动作本身被追踪到。
 *
 * 上一版是普通对象 + 一个 `packagesVersion` 版本号，靠调用方**记得**读它；
 * 那种约定必然漏（这次就是漏在 `NpArea` 与 `Playground` 的渲染上：
 * 卸载之后 DOM 还写着旧编辑器）。改成让**容器**可观测之后，
 * 每个读它的人自动进入依赖 —— 由构造保证，不靠纪律。
 *
 * ⚠️ `shallow`：值里放着组件（对象/函数），深代理会把组件定义也包一层，
 * 那会破坏组件身份比较。我们只需要"键的增删"被追踪。
 */
export const EDITOR_META: Record<string, EditorMeta> = shallowReactive<Record<string, EditorMeta>>({})

/** 按 `digit` 排好的编辑器清单（窗口类型菜单 / 快捷键渲染它）。⚠️ 同样可观测 */
export const EDITOR_ORDER: EditorType[] = reactive([])

/** 已实现的编辑器（模式栏、header 下拉用它）。⚠️ 同样可观测 */
export const IMPLEMENTED_EDITORS: EditorType[] = reactive([])

/**
 * **一条编辑器贡献的问题**。
 *
 * ⚠️ 形状和 `PACKAGE_CODE_PROBLEMS` / `ASSET_PROBLEMS` 一致（都是
 * 「哪来的 / 谁的问题 / 说人话的原因」），因为它们是同一类东西：
 * **校验期能查出来的、不该留给运行时的问题清单**。
 */
export interface EditorProblem {
  /** 贡献它的包 id */
  package: string
  /** 编辑器类型标识（可能是个连命名空间都没有的字符串） */
  editor: string
  message: string
}

/**
 * 编辑器贡献的问题清单 —— 正常应当是空的。
 *
 * ⚠️ 有问题的编辑器**照常注册**（`implemented: false`），不是整条消失：
 * 「半可用的包」比「包里的东西凭空不见」好排查得多 ——
 * 后者只会让人以为包没装上（和节点包的 `code` 编译不过同一条哲学）。
 *
 * ⚠️ 也是**可观测的**：问题清单出现/消失，界面（比如「节点包」的校验提示）
 * 应当跟着变 —— 理由同 `EDITOR_META`。
 */
export const EDITOR_PROBLEMS: EditorProblem[] = reactive([])

function sortByDigit(ids: EditorType[]): void {
  ids.sort((x, y) => (EDITOR_META[x]?.digit ?? 0) - (EDITOR_META[y]?.digit ?? 0))
}

/**
 * 把一批包的编辑器贡献合并进注册表（**这是唯一的写入口**）。
 *
 * 调用方是装配点 `playground/nodeTypes.ts`（它同时负责把包合进节点目录）——
 * 内核代码里只有那一个地方知道包的存在，`verify:boundary` 守着这条。
 *
 * 每次调用是**幂等重算**：先把表恢复成内核那几条，再铺本次的贡献。
 * 所以「卸载一个包」= 再调一次、且这批里没有它 —— 注册表自然回到内核那几条。
 *
 * ## 两种错误，两种反应（这是本节最要紧的一条）
 *
 * | 错的种类 | 反应 | 为什么 |
 * |---|---|---|
 * | **身份冲突**：类型为空 / 抢内核的类型 / 两个包抢同一个 | **抛错** | 语义上没法裁决 —— "两个包都叫 `graph`"没有正确答案，静默选一个的表现是「加载顺序变了，窗口就变了」 |
 * | **形状问题**：没给 `component` / 不支持 `header` / Region 拼错 / digit 撞车 | **记进 `EDITOR_PROBLEMS`，并把这条贡献降级成「未实现」** | 那是**一个编辑器**的问题，不该把整个程序带崩（和节点包的 `code` 编译不过、资产有问题同一条哲学：**半可用比整个消失好排查**） |
 *
 * 降级之后它在窗口类型菜单里**灰着**（`implemented: false`，现有的"未实现"机制），
 * 点不到，也就不会开出一个空白窗口或者一个关不掉的窗口。
 * `digit` 撞车则**自动让位**（分配下一个空号）并记一条问题 —— 顺序含糊是可修的，
 * 不值得把包废掉。
 *
 * ⚠️ **校验在前、铺表在后**：抛错时注册表**一个字节都没动**。
 * 半铺好的表比抛错难查得多 —— 那会表现成「窗口类型菜单里少了几个」，
 * 而错误现场已经过去了。
 *
 * ⚠️ **不带点的标识**是形状问题（记问题 + 照常注册）：内核里不许出现
 * 包贡献的编辑器标识，而那条检查（`verify:layers`）只能靠
 * 「带点的标识不会和通用词撞车」才做得完备 —— `if (x === 'page')` 里的
 * `'page'` 是不是编辑器，纯文本扫描分不出来。所以从源头要求命名空间
 * （`包.名字`），而 `verify:layers` 那条「贡献的标识都带点」**会让 CI 红**。
 */
export function registerEditorContributions(
  sources: readonly EditorContributionSource[],
): void {
  const usedDigits = new Set(Object.values(CORE_EDITOR_META).map((m) => m.digit))
  let nextDigit = Math.max(...usedDigits) + 1
  /** 类型标识 → 包 id（报错要点名是谁） */
  const owner = new Map<string, string>()
  /** 校验通过的贡献：类型标识 + 合并之后的元信息（② 一次性铺上去） */
  const accepted: { type: string; meta: EditorMeta }[] = []

  const problems: EditorProblem[] = []
  const report = (packageId: string, editor: string, message: string): void => {
    problems.push({ package: packageId, editor, message })
  }
  for (const source of sources) {
    for (const contribution of source.editors ?? []) {
      const type = contribution.type
      if (typeof type !== 'string' || type === '') {
        throw new Error(`包「${source.id}」贡献了一个没有 type 的编辑器`)
      }
      if (CORE_EDITOR_META[type as CoreEditorType]) {
        throw new Error(
          `包「${source.id}」的编辑器类型「${type}」和内核已有的撞了。` +
            '内核窗口的类型标识不能被包占用。',
        )
      }
      const previous = owner.get(type)
      if (previous) {
        throw new Error(
          `编辑器类型标识重复：「${type}」同时由包「${previous}」和「${source.id}」注册。`,
        )
      }
      owner.set(type, source.id)

      /* ── 从这里往下都是**形状问题**：记一条，然后尽量把它降级成一个能用的状态 ── */
      let usable = true
      if (!type.includes('.')) {
        report(
          source.id,
          type,
          '编辑器类型没有命名空间（应为 `包名.名字`）—— 「内核不许引用包贡献的编辑器」' +
            '那条静态检查靠"带点"才做得完备，裸标识会从它眼皮底下溜过去',
        )
      }
      if (!contribution.component) {
        usable = false
        report(
          source.id,
          type,
          '没有 component —— 贡献编辑器必须给出主体组件，否则那个窗口开了是一片空白',
        )
      }
      if (!contribution.regions.includes('header')) {
        usable = false
        report(
          source.id,
          type,
          '不支持 header —— 没有标题栏就切不出去、也关不掉那个窗口',
        )
      }
      const regions = contribution.regions.filter((key) => {
        if (ALL_REGIONS.includes(key)) return true
        report(
          source.id,
          type,
          `声明了不存在的 Region「${key}」（只有 ${ALL_REGIONS.join(' / ')}）—— ` +
            '那会渲染出一个永远不出现的开关，已忽略这一项',
        )
        return false
      })

      let digit = contribution.digit ?? nextDigit
      if (usedDigits.has(digit)) {
        report(
          source.id,
          type,
          `序号 ${digit} 已经被占了，自动改排到 ${nextDigit}（序号决定窗口类型菜单的顺序）`,
        )
        digit = nextDigit
      }
      usedDigits.add(digit)
      nextDigit = Math.max(nextDigit, digit + 1)

      accepted.push({
        type,
        meta: {
          label: contribution.label,
          icon: contribution.icon,
          digit,
          /*
           * ⚠️ **形状有问题的贡献降级成「未实现」**：菜单里灰着、点不到
           * （现有的"未实现"机制就是这么用的，见 `EditorMeta.implemented`）。
           * 它仍然占着类型标识 —— 那样"这个包声明过它"在界面上是看得见的，
           * 而不是整条悄悄消失。
           */
          implemented: usable,
          regions,
          ...(contribution.panels ? { panels: contribution.panels } : {}),
          ...(contribution.header ? { header: contribution.header } : {}),
          ...(contribution.tips ? { tips: contribution.tips } : {}),
          ...(contribution.defaultRegions ? { defaultRegions: contribution.defaultRegions } : {}),
          ...(usable && contribution.component ? { component: contribution.component } : {}),
          from: source.id,
        },
      })
    }
  }

  /* ① 恢复成内核那几条（原地：删键 + 铺新键） */
  for (const key of Object.keys(EDITOR_META)) delete EDITOR_META[key]
  Object.assign(EDITOR_META, CORE_EDITOR_META)
  /* ② 铺上本批贡献（到这里不可能再抛了） */
  for (const entry of accepted) EDITOR_META[entry.type] = entry.meta

  /* ③ 重建两个清单（原地，理由见文件头 ①） */
  EDITOR_ORDER.length = 0
  EDITOR_ORDER.push(...Object.keys(EDITOR_META))
  sortByDigit(EDITOR_ORDER)

  IMPLEMENTED_EDITORS.length = 0
  IMPLEMENTED_EDITORS.push(...EDITOR_ORDER.filter((e) => EDITOR_META[e]!.implemented))

  /* ④ 问题清单（原地更新，同上）。正常是空的 —— 装着的包都健康 */
  EDITOR_PROBLEMS.length = 0
  EDITOR_PROBLEMS.push(...problems)
}

/* 首次装配：只有内核那几条（包由装配点在启动时合并进来） */
registerEditorContributions([])

/* ══════════════════════════════════════════════════════════════════
   读接口
   ══════════════════════════════════════════════════════════════════ */

/**
 * 某个编辑器支持哪些 Region。
 *
 * ⚠️ **查不到就是「什么都不支持」**，不是抛错：布局里可能记着一个
 * **当前不在注册表里**的编辑器类型（它所属的包被卸载了）——
 * 那种窗口由 `editorOf()` 回落到默认编辑器，而在回落之前，
 * 这条读接口必须能安静地走完（`setEditor` / `setRegion` 都会读到）。
 */
export function editorRegionsOf(editor: EditorType): readonly RegionKey[] {
  return EDITOR_META[editor]?.regions ?? []
}

/** 这个编辑器类型**现在**认得吗（在合并注册表里） */
export function editorKnown(editor: EditorType): boolean {
  return EDITOR_META[editor] !== undefined
}

/**
 * 从「当前 → 默认 → 节点图」里挑第一个**认得**的。
 *
 * ## 为什么要有这一层（这是「窗口记意图，不记现状」的落点）
 *
 * `AreaLeaf.editor` 记的是**用户/预设要的那个窗口**，它可能属于一个
 * 已经卸载的包。三种反应里选了这一种：
 *
 * ```text
 *   ✗ 卸载时把 editor 改写成「节点图」   → 用户的意图没了，装回包也回不来
 *   ✗ 留一个「未安装」占位                → 松开「显示的编辑器一定是认得的」这条不变量
 *   ✓ 记着意图，**取的时候**回落           → 包装回来窗口自己就回来了
 * ```
 *
 * 于是"卸载包"不再需要任何补救动作（不用改写布局树），
 * 而界面拿到的永远是**认得的**编辑器 —— 见 `areas.ts` 的 `editorOf(area)`。
 *
 * ⚠️ 兜底是 `graph`：内核里那 8 条中最基础的一个，任何布局都还有东西可看。
 * 它是**最后**一环：连它都不在注册表里的话，那就是内核自己坏了
 * （`verify:layout` 有一条断言钉着「graph 一定在」）。
 */
export function resolveEditor(current: EditorType, fallback: EditorType = 'graph'): EditorType {
  if (editorKnown(current)) return current
  if (editorKnown(fallback)) return fallback
  return 'graph'
}

/** 按 `(编辑器, 侧, tabId)` 找一个 tab 定义（宿主渲染包贡献的面板要用） */
export function panelTabOf(
  editor: EditorType,
  side: PanelSide,
  tab: string,
): PanelTabDef | null {
  return EDITOR_META[editor]?.panels?.[side]?.find((t) => t.id === tab) ?? null
}

/**
 * 新建 Area 时的 Region 默认值。
 *
 * ⚠️ 最后一步**强制把不支持的 Region 关掉**，而不是指望
 * `EDITOR_META[x].defaultRegions` 把每一个都列全。
 * 那个写法漏一个就出 bug：`canvas` 支持 `['header','minimap']`，
 * 但它的 `defaultRegions` 只写了 `{ minimap: false }` —— 于是别的 Region
 * 从下面的 `base` 里带出 `true`，变成「画布上挂着一个它不支持的覆盖层开关」。
 * 这个 bug 是 `verify:layout` 的元数据自洽用例抓出来的。
 *
 * 由**构造**保证不变量，而不是靠约定 —— 这样以后新增编辑器时忘写一项也不会错。
 */
export function defaultRegions(editor: EditorType): RegionState {
  const meta = EDITOR_META[editor]
  const base: RegionState = {
    header: true,
    minimap: true,
  }
  const merged = { ...base, ...(meta?.defaultRegions ?? {}) }
  const allowed = new Set(meta?.regions ?? [])
  for (const key of ALL_REGIONS) {
    if (!allowed.has(key)) merged[key] = false
  }
  return merged
}
